import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import TrustBadge from "@/components/TrustBadge";
import { useAuth } from "@/contexts/AuthContext";
import { MARKETPLACE_CATEGORY_LABELS } from "@/lib/causes";
import { ArrowLeft, Coins, Pencil } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

export default function ListingDetail() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [sellerTrust, setSellerTrust] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/marketplace/listings/${id}`).then(({ data }) => {
      setL(data);
      if (data?.seller_id) {
        api
          .get(`/users/${data.seller_id}/profile`)
          .then(({ data: p }) => setSellerTrust(p.trust))
          .catch(() => setSellerTrust(null));
      }
    }).catch(() => setL(false));
  }, [id]);

  if (l === null) return <Layout><div className="p-10 text-ink-soft">Loading…</div></Layout>;
  if (l === false) return <Layout><div className="p-10">Not found.</div></Layout>;

  const isAuthed = user && typeof user === "object";
  const isSeller = isAuthed && l.seller_id === user.id;
  const balance = user?.time_hours_balance ?? 0;
  const canAfford = balance >= l.price_hours;

  const redeem = async () => {
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      await api.post("/marketplace/redeem", { listing_id: id });
      await refresh();
      setMsg({
        type: "ok",
        text: `Held ${l.price_hours} hours in escrow. They'll be released to ${l.seller_name} once you confirm receipt in Transactions.`,
      });
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div data-testid="listing-detail-page" className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <button onClick={() => nav(-1)} className="text-sm text-ink-soft inline-flex items-center gap-1 mb-6" data-testid="listing-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-8 card-soft overflow-hidden p-0">
          <div className="aspect-square md:aspect-auto bg-app">
            {l.image_url ? (
              <img src={resolveImageUrl(l.image_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center font-display text-6xl text-[var(--cm-ink)]/30">
                {l.title.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="p-8 md:p-10 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <span className="pill" data-testid="listing-category">{MARKETPLACE_CATEGORY_LABELS[l.category] || l.category}</span>
              <span className="pill capitalize">{l.kind}</span>
            </div>
            <h1 data-testid="listing-title" className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight">{l.title}</h1>
            <p className="text-ink-soft mt-2 flex items-center flex-wrap gap-2">
              <span>offered by <span className="font-semibold text-ink">{l.seller_name}</span></span>
              {sellerTrust && <TrustBadge trust={sellerTrust} size="sm" testId="seller-trust-badge" />}
            </p>

            <div className="mt-6">
              <TimeBadge hours={l.price_hours} size="lg" testId="listing-price" />
            </div>

            <p className="mt-6 leading-relaxed whitespace-pre-line text-ink" data-testid="listing-description">{l.description}</p>

            {msg.text && (
              <div
                data-testid="listing-message"
                className={`mt-6 text-sm px-4 py-3 rounded-lg ${
                  msg.type === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                {msg.text}
              </div>
            )}

            <div className="mt-auto pt-8">
              {!isAuthed ? (
                <>
                  <Link
                    to="/login"
                    state={{ from: { pathname: `/marketplace/${id}` } }}
                    data-testid="listing-signin-cta"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <Coins className="h-4 w-4" />
                    Sign in to redeem · {l.price_hours} hours
                  </Link>
                  <p className="text-xs text-ink-soft mt-3">
                    Browsing as a guest. Members earn Time Hours by volunteering and spend them
                    here.
                  </p>
                </>
              ) : isSeller ? (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="pill" data-testid="listing-self-label">This is your listing</span>
                  <Link
                    to={`/marketplace/${l.id}/edit`}
                    className="btn-outline text-sm inline-flex items-center gap-1.5"
                    data-testid="listing-edit-btn"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Link>
                  {l.active === false && (
                    <span className="text-xs text-ink-soft italic" data-testid="listing-inactive-label">
                      Hidden from the marketplace
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <button
                    data-testid="listing-redeem-btn"
                    onClick={redeem}
                    disabled={busy || !canAfford}
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <Coins className="h-4 w-4" />
                    {!canAfford ? `Need ${(l.price_hours - balance).toFixed(1)} more hours` : busy ? "…" : `Redeem for ${l.price_hours} hours`}
                  </button>
                  <p className="text-xs text-ink-soft mt-3">
                    Your hours are held in escrow until you confirm receipt. You can cancel anytime before then.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/transactions" className="text-sm text-[var(--cm-primary)] font-semibold" data-testid="listing-go-transactions">
            View your transactions →
          </Link>
        </div>
      </div>
    </Layout>
  );
}
